import { blogClient } from 'apollo/client';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  MAIN_DOMAIN,
  SITE_MAP,
} from 'globalConstants';
import {
  ARTICLES,
  ArticleEntityResponseCollection,
  ArticleRelationResponseCollection,
  ArticlesData,
  ArticlesVariables,
  MOST_VIEWED_ARTICLE,
  MostViewedArticleData,
  MostViewedArticleVariables,
  PublicationState,
} from 'graphql/blog/queries';
import { Omit, isEqual } from 'lodash';
import type { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { Context, Fragment, createContext } from 'react';
import { getBreadcrumbListJsonLD } from 'seo/schema';
import { detectDeviceByUserAgent } from 'utils';

const MobileHomeArticle = dynamic(() => import('components/mobile/home-article'));
const HomeArticle = dynamic(() => import('components/desktop/home-article'));

const DEFAULT_LIMIT_SPECIAL_CARD_ITEM = 5;
const DEFAULT_LIMIT_NEWEST_CARD_ITEM = 7;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req } = context;
  const { headers } = req;
  const device = detectDeviceByUserAgent(headers['user-agent']);
  let pageData = null;
  const [{ data: articles1Data }, { data: articles2Data }, { data: mostViewedArticleData }] =
    await Promise.all([
      blogClient.query<ArticlesData, ArticlesVariables>({
        query: ARTICLES,
        variables: {
          publicationState: PublicationState.LIVE,
          pagination: {
            pageSize: DEFAULT_LIMIT_SPECIAL_CARD_ITEM,
          },
          filters: {
            sticky: {
              eq: true,
            },
          },
          sort: ['publishedAt:desc'],
        },
      }),
      blogClient.query<ArticlesData, ArticlesVariables>({
        query: ARTICLES,
        variables: {
          publicationState: PublicationState.LIVE,
          pagination: {
            pageSize: DEFAULT_LIMIT_NEWEST_CARD_ITEM,
          },
          sort: ['publishedAt:desc'],
        },
      }),
      blogClient.query<MostViewedArticleData, MostViewedArticleVariables>({
        query: MOST_VIEWED_ARTICLE,
        variables: {
          publicationState: PublicationState.LIVE,
        },
      }),
    ]);
  const { articles: specialArticlesWithPagination } = articles1Data;
  const { articles: newestArticlesWithPagination } = articles2Data;
  const { articles: mostViewedArticlesWithPagination } =
    mostViewedArticleData.mostViewedArticle?.data?.attributes ?? {};

  pageData = {
    specialArticlesWithPagination: specialArticlesWithPagination ?? null,
    newestArticlesWithPagination: newestArticlesWithPagination ?? null,
    mostViewedArticlesWithPagination: mostViewedArticlesWithPagination ?? null,
  };

  return {
    props: {
      device,
      pageData,
    },
  };
};

export const HomeArticlePageContext: Context<Omit<Props, 'device'>> = createContext({});

interface Props {
  device?: 'mobile' | 'desktop';
  pageData?: {
    specialArticlesWithPagination?: ArticleEntityResponseCollection | null;
    newestArticlesWithPagination?: ArticleEntityResponseCollection | null;
    mostViewedArticlesWithPagination?: ArticleRelationResponseCollection | null;
  } | null;
}

const HomeArticlePage: NextPage = (props: Props) => {
  const { device, ...restProps } = props;
  const title = `Tin tức | ${DEFAULT_TITLE}`;
  const description = DEFAULT_DESCRIPTION;
  const ogUrl = `${MAIN_DOMAIN}/${SITE_MAP.BLOG.INDEX}`;
  const ogImage = DEFAULT_OG_IMAGE;
  const articleBreadcrumbJsonLD = getBreadcrumbListJsonLD([
    {
      slug: `${SITE_MAP.BLOG.INDEX}`,
      tooltip: 'Tin tức',
    },
  ]);

  return (
    <Fragment>
      <Head>
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
        />
        <meta name='description' content={description} />
        <meta name='og:title' content={title} />
        <meta name='og:description' content={description} />
        <meta name='og:url' content={ogUrl} />
        <meta name='og:image' content={ogImage} />
        <title>{title}</title>
      </Head>
      <HomeArticlePageContext.Provider value={restProps}>
        {isEqual(device, 'mobile') ? (
          <MobileHomeArticle />
        ) : isEqual(device, 'desktop') ? (
          <HomeArticle />
        ) : null}
      </HomeArticlePageContext.Provider>
      <script
        key='article-json-ld'
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: articleBreadcrumbJsonLD }}
      />
    </Fragment>
  );
};

export default HomeArticlePage;
